/**
 * Locale availability for a URL path. Edge-safe (no fs): content-backed
 * families read from the generated map, page slugs from locale-prefix sets.
 *
 * Shared by middleware (fallback redirect), geo-redirect, and locale
 * detection so they can never disagree and bounce a visitor between
 * /de/x and /x.
 */
import {
  CONTENT_LOCALE_AVAILABILITY,
  STATIC_ROUTE_LOCALE_AVAILABILITY,
} from "./locale-availability.generated";
import {
  GB_LOCALIZED_ONLY_BLOG_SLUGS,
  LOCALE_PREFIX,
  US_ONLY_BLOG_SLUGS,
} from "./locale-prefix";

const PREFIX_TO_LOCALE = Object.entries(LOCALE_PREFIX)
  .filter(([, prefix]) => prefix !== "")
  .map(([locale, prefix]) => ({ locale, prefix }));

const MARKDOWN_SUFFIX = ".md";

export interface SplitLocalePath {
  locale: string;
  /** Path without locale prefix or `.md` suffix, e.g. `/blog/foo`. */
  barePath: string;
  /** `.md` when the request targets a Markdown twin, otherwise empty. */
  suffix: string;
}

/** Split a pathname into locale, bare path and Markdown suffix. */
export function splitLocalePath(pathname: string): SplitLocalePath {
  let locale = "en";
  let rest = pathname;

  for (const { locale: candidate, prefix } of PREFIX_TO_LOCALE) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) {
      locale = candidate;
      rest = pathname.slice(prefix.length) || "/";
      break;
    }
  }

  // Collapse repeated slashes so a path like /de//broker can never yield a
  // protocol-relative "//broker" that a URL parser reads as a host.
  rest = rest.replace(/\/{2,}/g, "/");

  const suffix = rest.endsWith(MARKDOWN_SUFFIX) ? MARKDOWN_SUFFIX : "";
  const barePath = suffix ? rest.slice(0, -MARKDOWN_SUFFIX.length) : rest;
  return { locale, barePath: barePath || "/", suffix };
}

/**
 * Locales a bare path is available in, or `null` when the path is not
 * locale-restricted (or unknown, which should 404 naturally).
 */
export function getAvailableLocales(
  barePath: string
): readonly string[] | null {
  const segments = barePath.split("/").filter(Boolean);
  const family = segments[0] ?? "";
  const slug = segments[1];

  // Only known static routes are restricted. Unknown descendants such as
  // /broker/typo have no route, so they must keep returning 404.
  const staticRoute = segments.join("/");
  if (Object.hasOwn(STATIC_ROUTE_LOCALE_AVAILABILITY, staticRoute)) {
    return STATIC_ROUTE_LOCALE_AVAILABILITY[staticRoute] ?? null;
  }

  const familySlugs = CONTENT_LOCALE_AVAILABILITY[family];
  if (familySlugs && segments.length === 2 && slug !== undefined) {
    const known = Object.hasOwn(familySlugs, slug)
      ? familySlugs[slug]
      : undefined;
    if (known) return known;

    // Slug not in the generated map: keep the static blog gates as a backstop.
    if (family === "blog") {
      if (US_ONLY_BLOG_SLUGS.has(slug)) return ["en"];
      if (GB_LOCALIZED_ONLY_BLOG_SLUGS.has(slug)) return ["en", "en-GB"];
    }
    return null;
  }

  return null;
}

/** Whether a bare path is available in the given locale. */
export function isPathAvailableInLocale(
  barePath: string,
  locale: string
): boolean {
  const available = getAvailableLocales(barePath);
  return available === null || available.includes(locale);
}

/**
 * English fallback for a locale-prefixed pathname whose page does not exist
 * in that locale, or `null` when no redirect applies. Only returns a path
 * when the English page really exists, so GB-only pages and typos still 404.
 */
export function getLocaleFallbackPath(pathname: string): string | null {
  const { locale, barePath, suffix } = splitLocalePath(pathname);
  if (locale === "en") return null;

  const available = getAvailableLocales(barePath);
  if (available === null || available.includes(locale)) return null;
  if (!available.includes("en")) return null;

  return `${barePath}${suffix}`;
}
